import { useState, useEffect, useCallback } from 'react';

export const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSlowNetwork, setIsSlowNetwork] = useState(false);
    const [networkSpeed, setNetworkSpeed] = useState(null);

    // Test network speed by measuring ping time
    const testNetworkSpeed = useCallback(async () => {
        if (!navigator.onLine) {
            setIsSlowNetwork(false);
            setNetworkSpeed(null);
            return;
        }

        try {
            const startTime = performance.now();

            // Use a small image or endpoint to test speed
            // Adding timestamp to prevent caching
            const response = await fetch(`${window.location.origin}/assets/icons/favicon.ico?t=${Date.now()}`, {
                method: 'HEAD',
                cache: 'no-cache'
            });

            const endTime = performance.now();
            const pingTime = endTime - startTime;

            setNetworkSpeed(pingTime);

            // Consider network slow if ping > 900ms (0.9 seconds)
            const isSlow = pingTime > 900;
            setIsSlowNetwork(isSlow);

        } catch (error) {
            console.warn('Network speed test failed:', error);
            // If test fails, assume slow network
            setIsSlowNetwork(true);
            setNetworkSpeed(null);
        }
    }, []);

    const checkConnection = useCallback(() => {
        const online = navigator.onLine;
        setIsOnline(online);

        if (online) {
            testNetworkSpeed();
        } else {
            setIsSlowNetwork(false);
            setNetworkSpeed(null);
        }
    }, [testNetworkSpeed]);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            testNetworkSpeed();
        };

        const handleOffline = () => {
            setIsOnline(false);
            setIsSlowNetwork(false);
            setNetworkSpeed(null);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initial network speed test
        if (navigator.onLine) {
            testNetworkSpeed();
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [testNetworkSpeed]);

    return {
        isOnline,
        isSlowNetwork,
        networkSpeed,
        checkConnection,
        testNetworkSpeed
    };
};